import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { PageLayout, PageLayoutPrefs, pageLayoutStorageKey, sanitizePageLayoutPrefs, SplitLayout, clampPaneRatio } from './pageLayout';

/** Every mounted consumer (the profile menu tiles, the page) re-reads when one of them writes. */
const CHANGED_EVENT = 'pageLayout:changed';

const read = (key: string): PageLayoutPrefs => {
  try {
    const raw = window.localStorage.getItem(key);
    return sanitizePageLayoutPrefs(raw ? JSON.parse(raw) : undefined);
  } catch {
    return sanitizePageLayoutPrefs(undefined);
  }
};

/**
 * The user's page layout and splitter ratios (see pageLayout.ts), from this browser's storage. Changing
 * it in the profile menu updates an open page at once — no reload.
 */
export const usePageLayout = () => {
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const key = pageLayoutStorageKey(loggedUser?.id ?? 'anonymous');
  const [prefs, setPrefs] = useState<PageLayoutPrefs>(() => read(key));

  useEffect(() => {
    setPrefs(read(key));
    const sync = () => setPrefs(read(key));
    window.addEventListener(CHANGED_EVENT, sync);
    // Another tab of the same user changing it.
    const onStorage = (event: StorageEvent) => event.key === key && sync();
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGED_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, [key]);

  const write = useCallback(
    (next: PageLayoutPrefs) => {
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Quota or privacy mode: the choice still applies to this page until it is reloaded.
      }
      setPrefs(next);
      window.dispatchEvent(new Event(CHANGED_EVENT));
    },
    [key],
  );

  const setLayout = useCallback((layout: PageLayout) => write({ ...read(key), layout }), [key, write]);
  const setRatio = useCallback(
    (split: SplitLayout, ratio: number) => {
      const current = read(key);
      write({ ...current, ratio: { ...current.ratio, [split]: clampPaneRatio(ratio, current.ratio[split]) } });
    },
    [key, write],
  );

  return { ...prefs, setLayout, setRatio };
};

export default usePageLayout;
